// @ts-check

/**
 * Dispatches actions to refresh the campaign preview via WebSocket.
 *
 * Currently used in:
 * - The Define Filters page (after clicking Save/Update Campaign button)
 * - The Campaign Details page (when clicking Refresh Campaign button)
 *
 * What this does:
 * 1. Sends a WebSocket message to trigger a new query preview
 * 2. Sets campaign preview loading state
 * 3. Handles downstream WebSocket responses (e.g., query_running, query_results)
 * 4. If query results are received, dispatches a Redux thunk to fetch updated campaign data
 *
 * To observe:
 * - Open browser dev tools > Network tab > WS messages
 * - You'll see one upstream message and two downstream:
 *   - detail_type: "query_running"
 *   - detail_type: "query_results"
 * - These messages will include a new `statement_id`
 */

import {
  setCampaignPreviewLoading,
  setCampaignPreviewWebsocket,
  setCalculatedMatchSummaryWebsocket,
  setWebsocketLoading,
  getCampaignAsync,
} from 'core/store/slices';
import { formatQueryCampaignMessage, wsResponseType } from 'core';

/**
 * Refreshes the campaign preview via WebSocket and dispatches updates to Redux.
 *
 * @param {{
 *   campaign: CampaignModel,
 *   sendMessage: (params: {
 *     handleError: boolean,
 *     message: any,
 *     onMessageCallback: (evt: any) => void
 *   }) => void,
 *   dispatch: Function
 * }} params
 * @returns {void}
 */
export function campaignRefresh({ campaign, sendMessage, dispatch }) {
  if (!campaign?.campaignId || !campaign?.logic) return;

  dispatch(setCampaignPreviewLoading(true));
  // Set the websocket loading state until the new campaign filter query data is returned.
  dispatch(setWebsocketLoading(true));

  const message = formatQueryCampaignMessage(campaign);

  sendMessage({
    handleError: true,
    message,
    onMessageCallback: (evt) => {
      dispatch(
        setCampaignPreviewWebsocket({
          wsData: evt.data,
          queryLogic: campaign?.logic,
        }),
      );
      dispatch(setCalculatedMatchSummaryWebsocket({ wsData: evt.data }));

      if (evt.data.detail_type === wsResponseType.QUERY_RESULTS) {
        dispatch(getCampaignAsync({ campaignId: campaign.campaignId }));
      }
    },
  });
}
